import Model from "../../Model.js";
import optionsBag from "../../options/optionsBag.js";
import PeriodReportEventLog from "./event/PeriodReportEventLog.js";
import Timespan from "../../period/Timespan.js";

/**
 * `periodReportSummary` represent a period report in a light-weight manner.
 *
 * @param {string} spec.periodType - Period covered by the report in smart string format
 * @param {number} spec.periodIndex - Period covered by the report in smart string format
 * @param {timespan} spec.period - Period covered by the report in timespan format
 * @param {string} spec.author - Name of the report author
 * @param {string} spec.title - Report title
 * @param {string} spec.id - Unique identifier of the report
 * @param {periodReportEventLog} spec.eventLog - The event log this describes the state of the report
 */
export default class PeriodReportSummary extends Model {
	constructor(spec) {
		super(spec);
		this._period = spec.period;
		this._author = spec.author;
		this._periodType = spec.periodType;
		this._periodIndex = spec.periodIndex;
		this._eventLog = spec.eventLog;
		this._title = spec.title || "";
		this._id = spec.id;

		this._isAcknowledgementEnabled = optionsBag
			.fromId("acknowledge-period-report")
			.isActive();
	}

	accept(visitor) {
		return visitor.visitPeriodReportSummary(this);
	}

	getTitle() {
		return this._title;
	}

	getId() {
		return this._id;
	}

	getPeriodType() {
		return this._periodType;
	}

	getPeriodIndex() {
		return this._periodIndex;
	}

	getPeriod() {
		return this._period;
	}

	getAuthor() {
		return this._author;
	}

	getViewByCompanyUserEvents() {
		return this._eventLog.getViewByCompanyUserEvents();
	}

	getAcknowledgeEvents() {
		return this._eventLog.getAcknowledgeEvents();
	}

	getLastUpdateDate() {
		return this._eventLog.getLastUpdateDate();
	}

	getPublishDate() {
		return this._eventLog.getPublishDate();
	}

	isWithdrawn() {
		return this._eventLog.isWithdrawn();
	}

	isDraft() {
		return this._eventLog.isDraft();
	}

	isPublished() {
		return this._eventLog.isPublished();
	}

	isPublishedBefore(other) {
		return this.getPublishDate() >= other.getPublishDate();
	}

	isViewedByCompanyUser() {
		return this._eventLog.isViewedByCompanyUser();
	}

	isAcknowledged() {
		return (
			this._isAcknowledgementEnabled && this._eventLog.isAcknowledged()
		);
	}

	isNull() {
		return false;
	}

	// TODO associate periodType strings with period types
	isMonthReport() {
		return this._periodType === "Month";
	}

	isBimesterReport() {
		return this._periodType === "Bimester";
	}

	isQuarterReport() {
		return this._periodType === "Quarter";
	}

	isHalfYearReport() {
		return this._periodType === "HalfYear";
	}

	isYearReport() {
		return this._periodType === "Year";
	}

	isConsecutiveMonthsReport() {
		return this._periodType === "ConsecutiveMonths";
	}

	/**
	 * Since we are using RPC like resources for the report, all we need to
	 * send  back to the backend is the `id` value.
	 */
	serialize() {
		return {
			id: this._id,
		};
	}

	static transformMaterializationData(data) {
		data.period = new Timespan(data.period);
		data.eventLog = PeriodReportEventLog.materialize(data.eventLog);
	}
}
